import { Dispatch, FC, Fragment, useEffect, useState } from "react";
import { Box, FormControl, Grid, InputLabel, LinearProgress, Select, TextField } from "@mui/material";
import { useStyles } from '../styles';
import { LoadingButton } from "@mui/lab";
import BorderColorIcon from '@mui/icons-material/BorderColor';
import { AlertType } from "../../../types/AlertType";
import { AxiosInstance } from "axios";
import CheckAttr from "../../../utils/CheckAttr";
import { AllMounth, PROFIL_MANAGER, articlesSansActes, yearsToDisplay } from '../../../constants/Constants';
import { BudgetData } from "../../../types/DataBudget";
import {
    calculCABudget, calculCoutBudget, calculHeureProductionBudget,
    calculMargeValeurBudget, calculTauxMargeBudget
} from "../../../utils/utils";
import { isValidInput } from "./AddBudgetReforecastForm";

interface BudgetToSave {
    id?: number;
    pays: string;
    client: string;
    operation: string;
    article_id?: number;
    article: string;
    type?: string;
    year: number | null;
    month: number | null;
    ca_budget: string | null;
    tx_marge_budget: number | null;
    marge_valeur_budget: string | null;
    heure_production_budget: string | null;
    heure_ca_budget: number | null;
    acte_budget: number | null;
    heure_cout_budget: string | null;
    cout_budget: string | null;
    dmt?: string | null;
    taux_occupation?: number | string | null;
    taux_conges?: number | string | null;
    taux_absenteisme?: number | string | null;
    model?: string;
}

interface Tarifification {
    id: number | null;
    tarifheure: string;
    tarifacte: string;
}

interface Cout {
    id: number;
    coutactivite: string;
}

interface InputValidation {
    pays: boolean;
    client: boolean;
    operation: boolean;
    article: boolean;
    month: boolean;
    year: boolean;
    ca_budget: boolean;
    heure_cout_budget: boolean;
    dmt?: boolean;
    taux_occupation?: boolean;
    taux_conges?: boolean;
    taux_absenteisme?: boolean;
    // tx_marge_budget: boolean;
    // marge_valeur_budget: boolean;
}

interface SaisieAgentFormProps {
    onClose: () => void;
    budget?: BudgetData;
    http: AxiosInstance;
    model: string;
    profil: string;
    setOpenSnack: Dispatch<React.SetStateAction<boolean>>;
    setAlerteMessage: Dispatch<React.SetStateAction<string>>;
    setAlertType: Dispatch<React.SetStateAction<AlertType>>;
    reloadList: (reloadMenuItem?: boolean) => void;
}

const resetBudgetToSave: BudgetToSave = {
    id: undefined,
    pays: '',
    client: '',
    operation: '',
    article: '',
    month: null,
    year: null,
    ca_budget: '',
    tx_marge_budget: null,
    marge_valeur_budget: '',
    heure_production_budget: null,
    heure_ca_budget: null,
    acte_budget: null,
    heure_cout_budget: null,
    cout_budget: null,
    dmt: null,
    taux_occupation: null,
    taux_conges: null,
    taux_absenteisme: null
};

const resetInpuValidation: InputValidation = {
    pays: false,
    client: false,
    operation: false,
    article: false,
    month: false,
    year: false,
    ca_budget: false,
    heure_cout_budget: false,
    dmt: false,
    taux_occupation: false,
    taux_conges: false,
    taux_absenteisme: false
    // tx_marge_budget: false,
    // marge_valeur_budget: false,
};

const formatDmtForTimeInput = (dmt: string | null | undefined): string => {
    if (!dmt) return "";
    const parts = dmt.split(":");
    if (parts.length >= 2) {
        const hh = parts[0].padStart(2, "0");
        const mm = parts[1].padStart(2, "0");
        const ss = parts[2] ? parts[2].padStart(2, "0") : "00";
        return `${hh}:${mm}:${ss}`;
    }
    return dmt;
};

const DuplicateBudgetForm: FC<SaisieAgentFormProps> = ({
    budget,
    http,
    model,
    profil,
    onClose,
    reloadList,
    setOpenSnack,
    setAlerteMessage,
    setAlertType,
}) => {
    const classes = useStyles();

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isClickSave, setIsClickSave] = useState<boolean>(false);

    const [tarifParam, setTarifParam] = useState<Tarifification>();
    const [coutParam, setCoutParam] = useState<Cout>();
    const [budgetToSave, setBudgetToSave] = useState<BudgetToSave>(resetBudgetToSave);
    const [errorInput, setErrorInput] = useState<InputValidation>(resetInpuValidation);

    const [inputIsUpdated, setInputIsUpdated] = useState<boolean>(false);

    // Heures de management non facturées : Heure CA, Acte, PU Heure et PU Acte non saisis (à 0), CA = 0
    const sansActes = articlesSansActes.includes(budget?.article_libelle as string);

    useEffect(() => {
        if (inputIsUpdated) {
            const coutBudget = calculCoutBudget(budgetToSave?.heure_cout_budget, coutParam?.coutactivite)
            const margeValeurBudget = calculMargeValeurBudget(budgetToSave?.ca_budget, budgetToSave?.cout_budget);
            // CA = 0 pour les articles sans actes : taux de marge non calculable
            const tauxBudget = sansActes ? null : calculTauxMargeBudget(margeValeurBudget, budgetToSave?.ca_budget);
            const heureProdBudget = calculHeureProductionBudget(budgetToSave?.ca_budget, tauxBudget, coutParam?.coutactivite);

            setBudgetToSave({
                ...budgetToSave,
                cout_budget: coutBudget,
                tx_marge_budget: tauxBudget,
                marge_valeur_budget: margeValeurBudget,
                heure_production_budget: heureProdBudget
            });
        }
    }, [
        budgetToSave?.ca_budget,
        budgetToSave?.cout_budget,
        coutParam?.coutactivite
    ]);

    useEffect(() => {
        if (budget) {
            const data = {
                article: budget?.article_id,
                year: budget?.annee,
                month: budget?.mois
            };

            http.post('budget/get-tarif-cout/for-one-budget', data)
                .then(res => {
                    const { tarif, cout } = res.data;
                    setTarifParam(sansActes ? { ...tarif, id: tarif?.id ?? null, tarifheure: "0", tarifacte: "0" } : tarif);
                    setCoutParam(cout);
                }).catch(error => {
                    console.log(error);
                });

            setBudgetToSave({
                year: budget?.annee,
                month: null,
                pays: budget?.pays_libelle,
                client: budget?.client_libelle ?? budget?.client_budget_libelle,
                operation: budget?.operation_libelle ?? budget?.operation_budget_libelle,
                article_id: budget?.article_id,
                article: budget?.article_libelle,
                type: budget?.type_libelle,
                ca_budget: sansActes ? "0.00" : parseFloat(budget?.ca_budget).toFixed(2),
                tx_marge_budget: (!sansActes && budget?.tx_marge_budget) ? Number(budget?.tx_marge_budget) : null,
                marge_valeur_budget: sansActes ? calculMargeValeurBudget("0.00", budget?.cout_budget) : budget?.marge_valeur_budget,
                heure_production_budget: budget?.heure_prod_budget,
                heure_ca_budget: sansActes ? 0 : (budget?.qte_heure_budget ? Number(budget?.qte_heure_budget) : null),
                acte_budget: sansActes ? 0 : (budget?.qte_acte_budget ? Number(budget?.qte_acte_budget) : null),
                cout_budget: budget?.cout_budget ? budget?.cout_budget : null,
                heure_cout_budget: budget?.heure_cout_budget ? String(budget?.heure_cout_budget) : null,
                dmt: budget?.dmt ?? null,
                taux_occupation: budget?.taux_occupation !== null && budget?.taux_occupation !== undefined ? Number(budget?.taux_occupation) : null,
                taux_conges: budget?.taux_conges !== null && budget?.taux_conges !== undefined ? Number(budget?.taux_conges) : null,
                taux_absenteisme: budget?.taux_absenteisme !== null && budget?.taux_absenteisme !== undefined ? Number(budget?.taux_absenteisme) : null,
                model
            });

            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        let heureCoutBudget: number | null = null;
        let coutBudget: number | null = null;
        if (inputIsUpdated) {
            if (budget?.heure_cout_budget) {
                heureCoutBudget = budget?.heure_cout_budget;
            } else {
                if (budget?.ca_budget && budget?.tx_marge_budget && coutParam?.coutactivite) {

                    coutBudget = Number(budget?.ca_budget) * (1 - Number(budget?.tx_marge_budget));
                    // heureCoutBudget = Number(budget?.ca_budget) / ((Number(budget?.tx_marge_budget) * Number(coutParam?.coutactivite)) + Number(coutParam?.coutactivite));
                    heureCoutBudget = coutBudget / Number(coutParam?.coutactivite);
                    setBudgetToSave({
                        ...budgetToSave,
                        heure_cout_budget: heureCoutBudget.toFixed(2),
                        cout_budget: String(coutBudget.toFixed(2))
                        // cout_budget: String((heureCoutBudget * Number(coutParam?.coutactivite)).toFixed(2))
                    })
                }
            }
        }

    }, [coutParam?.coutactivite]);

    const handleChange = (e: any) => {
        const { name, value } = e.target;

        setInputIsUpdated(true);

        const val = value.replace(",", ".");

        switch (name) {
            case 'heure_ca_budget':
                setBudgetToSave({
                    ...budgetToSave,
                    heure_ca_budget: val,
                    ca_budget: calculCABudget(val, tarifParam?.tarifheure)
                });
                break;
            case 'acte_budget':
                setBudgetToSave({
                    ...budgetToSave,
                    acte_budget: val,
                    ca_budget: calculCABudget(val, tarifParam?.tarifacte)
                });
                break;
            case 'heure_cout_budget':
                setBudgetToSave({
                    ...budgetToSave,
                    heure_cout_budget: val,
                    cout_budget: calculCoutBudget(val, coutParam?.coutactivite)
                });
                break;
            case 'taux_occupation':
            case 'taux_conges':
            case 'taux_absenteisme':
                setBudgetToSave({
                    ...budgetToSave,
                    [name]: val !== "" ? val : null
                });
                break;
            case 'dmt':
                setBudgetToSave({
                    ...budgetToSave,
                    dmt: value !== "" ? value : null
                });
                break;
            default:
                setBudgetToSave({
                    ...budgetToSave,
                    [name]: value
                });
        };
    }

    const handleChangeTarif = (e: any) => {
        const { name, value } = e.target;

        const val = value.replace(",", ".");

        if (isValidInput(val)) {
            setTarifParam({
                ...tarifParam as Tarifification,
                [name]: val
            });

            switch (name) {
                case "tarifheure":
                    setBudgetToSave({
                        ...budgetToSave,
                        ca_budget: calculCABudget(budgetToSave?.heure_ca_budget, val)
                    });
                    break;
                case "tarifacte":
                    setBudgetToSave({
                        ...budgetToSave,
                        ca_budget: calculCABudget(budgetToSave?.acte_budget, val)
                    });
                    break;

                default:
                    break;
            }
        }
    }

    const handleChandeCout = (e: any) => {
        const value = e.target.value;

        const val = value.replace(",", ".");

        setInputIsUpdated(true)

        if (isValidInput(val)) {
            setCoutParam({
                ...coutParam as Cout,
                coutactivite: val
            });

            setBudgetToSave({
                ...budgetToSave,
                cout_budget: calculCoutBudget(budgetToSave?.heure_cout_budget, val)
            });
        }
    };

    const isValidate = (): boolean => {
        // Reinitialise les variables des validations
        setErrorInput(resetInpuValidation);

        if (
            !CheckAttr(budgetToSave?.pays) ||
            !CheckAttr(budgetToSave?.article) ||
            !CheckAttr(budgetToSave?.client) ||
            !CheckAttr(budgetToSave?.operation) ||
            budgetToSave?.year === 0 ||
            !CheckAttr(budgetToSave?.month) ||
            !CheckAttr(budgetToSave?.heure_cout_budget)) {
            if (!CheckAttr(budgetToSave?.pays)) {
                setErrorInput((prev: InputValidation) => ({ ...prev, pays: true }));
            }

            if (!CheckAttr(budgetToSave?.client)) {
                setErrorInput((prev: InputValidation) => ({ ...prev, client: true }));
            }

            if (!CheckAttr(budgetToSave?.operation)) {
                setErrorInput((prev: InputValidation) => ({ ...prev, operation: true }));
            }

            if (!CheckAttr(budgetToSave?.article)) {
                setErrorInput((prev: InputValidation) => ({ ...prev, bu: true }));
            }

            if (!CheckAttr(budgetToSave?.month)) {
                setErrorInput((prev: InputValidation) => ({ ...prev, month: true }));
            }

            if (budgetToSave?.year === 0) {
                setErrorInput((prev: InputValidation) => ({ ...prev, year: true }));
            }

            if (!CheckAttr(budgetToSave?.ca_budget)) {
                setErrorInput((prev: InputValidation) => ({ ...prev, ca_budget: true }));
            }

            if (!CheckAttr(budgetToSave?.heure_cout_budget)) {
                setErrorInput((prev: InputValidation) => ({ ...prev, heure_cout_budget: true }));
            }

            setAlerteMessage("Merci de remplir les champs en rouge !");
            setAlertType("warning");
            setOpenSnack(true);

            return false;
        }

        const dmtReg = /^([0-9]{1,3}):[0-5][0-9](:[0-5][0-9])?$/;
        let dmtErr = false;
        if (CheckAttr(budgetToSave?.dmt) && !dmtReg.test(String(budgetToSave?.dmt))) {
            dmtErr = true;
            setErrorInput((prev: InputValidation) => ({ ...prev, dmt: true }));
        }

        const isValidRate = (val: any) => {
            if (!CheckAttr(val)) return true;
            const num = Number(val);
            return !isNaN(num) && num >= 0 && num <= 100;
        };

        let occErr = false;
        if (!isValidRate(budgetToSave?.taux_occupation)) {
            occErr = true;
            setErrorInput((prev: InputValidation) => ({ ...prev, taux_occupation: true }));
        }
        let cgErr = false;
        if (!isValidRate(budgetToSave?.taux_conges)) {
            cgErr = true;
            setErrorInput((prev: InputValidation) => ({ ...prev, taux_conges: true }));
        }
        let absErr = false;
        if (!isValidRate(budgetToSave?.taux_absenteisme)) {
            absErr = true;
            setErrorInput((prev: InputValidation) => ({ ...prev, taux_absenteisme: true }));
        }

        if (dmtErr || occErr || cgErr || absErr) {
            let validationMessage = "Merci de remplir les champs en rouge !";
            if (dmtErr) {
                validationMessage = "Format DMT incorrect (attendu: HH:MM:SS ou HH:MM) !";
            } else {
                validationMessage = "Les taux doivent être compris entre 0 et 100% !";
            }
            setAlerteMessage(validationMessage);
            setAlertType("warning");
            setOpenSnack(true);
            return false;
        }

        return true;
    }

    const handleSave = () => {
        if (isValidate()) {
            setIsClickSave(true);

            http.post(`budget/check-existence`, { ...budgetToSave, duplicate: true })
                .then(res => {
                    const { count } = res.data;

                    if (count > 0) {
                        setAlerteMessage(`Il y avait déjà un budget sur ce mois et année !`);
                        setAlertType("warning");
                        setOpenSnack(true);
                        setIsClickSave(false);
                    } else {
                        const data = {
                            budgetToSave,
                            tarifParam,
                            coutParam
                        }

                        http.post('budget/duplicate', data)
                            .then(() => {
                                setIsClickSave(false);
                                setAlerteMessage("Enregistrement efféctuée !");
                                setAlertType("success");
                                setOpenSnack(true);
                                onClose();
                                reloadList(false);
                            }).catch(error => {
                                console.log(error);
                                setAlertType("error");
                                setOpenSnack(true);
                            });
                    }
                }).catch(error => {
                    console.log(error);
                    setAlertType("error");
                    setOpenSnack(true);
                    setIsClickSave(false);
                });
        }
    };

    /**
     * Les champs PU et Cout sont grisés si je suis Manager
     */
    const disableInputOfManager = () => {
        if (profil === PROFIL_MANAGER) {
            return true;
        }

        return false;
    }

    const disablePrixUnitaireInput = () => {
        let disableHeureInput = false;
        let disableActeInput = false;
        if (budget?.code_article_libelle) {
            const lastCharactersOfCodeArticle = budget?.code_article_libelle?.slice(-1).toLowerCase();

            disableHeureInput = lastCharactersOfCodeArticle === "a";
            disableActeInput = lastCharactersOfCodeArticle === "h";
        } else {
            disableHeureInput = CheckAttr(tarifParam?.tarifacte);
            disableActeInput = CheckAttr(tarifParam?.tarifheure);
        }

        return {
            disableHeureInput,
            disableActeInput
        }
    };

    const disablePUInput = disablePrixUnitaireInput();

    /**
     * Logique page loading...........................
     */
    const progress = (
        <div className={classes.loading}>
            <LinearProgress color="error" sx={{ my: 2 }} />
            <LinearProgress color="warning" />
        </div>
    );

    const formContent = (
        <Fragment>
            <Box className={classes.root}>
                <Grid container spacing={2} columns={{ xs: 6, sm: 12, md: 4 }}>
                    <Grid item xs={6} sm={6} md={2}>
                        <TextField
                            label="Pays de production"
                            color="warning"
                            fullWidth
                            error={errorInput.pays}
                            value={budgetToSave?.pays}
                            onChange={handleChange}
                            disabled
                            InputLabelProps={{
                                shrink: true,
                            }}
                        />

                        <TextField
                            label="Client"
                            color="warning"
                            fullWidth
                            error={errorInput.client}
                            value={budgetToSave?.client}
                            onChange={handleChange}
                            disabled
                            InputLabelProps={{
                                shrink: true,
                            }}
                        />

                        <TextField
                            label="Operation"
                            color="warning"
                            fullWidth
                            error={errorInput.operation}
                            value={budgetToSave?.operation}
                            onChange={handleChange}
                            disabled
                            InputLabelProps={{
                                shrink: true,
                            }}
                        />

                        <TextField
                            label="Désignation / Article"
                            color="warning"
                            fullWidth
                            error={errorInput.article}
                            value={budgetToSave?.article}
                            onChange={handleChange}
                            disabled
                            InputLabelProps={{
                                shrink: true,
                            }}
                        />

                        <TextField
                            label="Type Article"
                            color="warning"
                            fullWidth
                            value={budgetToSave?.type}
                            disabled
                            InputLabelProps={{
                                shrink: true,
                            }}
                        />

                        <FormControl
                            variant="outlined"
                            className={classes.formControl}
                            sx={{ marginLeft: 1, marginTop: 1 }}
                        >
                            <InputLabel
                                htmlFor="year"
                                color="warning"
                                shrink={CheckAttr(budgetToSave?.year)}
                            >
                                Année
                            </InputLabel>
                            <Select
                                id="year"
                                native
                                color="warning"
                                value={budgetToSave?.year}
                                onChange={handleChange}
                                error={errorInput.year}
                                disabled
                                label="Année"
                                inputProps={{
                                    name: "year",
                                }}
                            >
                                <option value={0}></option>
                                {yearsToDisplay.map((year) => (
                                    <option key={year} value={year}>{year}</option>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl
                            variant="outlined"
                            className={classes.formControl}
                            sx={{ marginLeft: 1, marginTop: 1 }}
                        >
                            <InputLabel
                                htmlFor="month"
                                color="warning"
                                shrink={CheckAttr(budgetToSave?.month)}
                            >
                                Mois
                            </InputLabel>
                            <Select
                                id="month"
                                native
                                color="warning"
                                value={budgetToSave?.month}
                                onChange={handleChange}
                                error={errorInput.month}
                                label="Mois"
                                inputProps={{
                                    name: "month",
                                }}
                            >
                                <option value=""></option>
                                {AllMounth.map((month) => (
                                    <option key={month?.id} value={month?.id}>
                                        {month?.mounth}
                                    </option>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField
                            label="Heure CA Budget"
                            color="warning"
                            fullWidth
                            // error={errorInput.ca_budget}
                            name="heure_ca_budget"
                            value={budgetToSave?.heure_ca_budget}
                            autoComplete="off"
                            placeholder="Ex: 2.02"
                            onChange={handleChange}
                            disabled={sansActes || CheckAttr(tarifParam?.tarifacte)}
                            InputLabelProps={{
                                shrink: CheckAttr(budgetToSave?.heure_ca_budget),
                            }}
                        />

                        <TextField
                            label="Acte Budget"
                            color="warning"
                            fullWidth
                            // error={errorInput.ca_budget}
                            name="acte_budget"
                            value={budgetToSave?.acte_budget}
                            autoComplete="off"
                            placeholder="Ex: 2.02"
                            onChange={handleChange}
                            disabled={sansActes || CheckAttr(tarifParam?.tarifheure)}
                            InputLabelProps={{
                                shrink: CheckAttr(budgetToSave?.acte_budget),
                            }}
                        />

                        <TextField
                            label="PU Heure"
                            color="warning"
                            name="tarifheure"
                            fullWidth
                            value={tarifParam?.tarifheure}
                            disabled={sansActes || disablePUInput?.disableHeureInput || disableInputOfManager()}
                            onChange={handleChangeTarif}
                            InputLabelProps={{
                                shrink: true,
                            }}
                        />

                        <TextField
                            label="PU Acte"
                            color="warning"
                            name="tarifacte"
                            fullWidth
                            value={tarifParam?.tarifacte}
                            disabled={sansActes || disablePUInput?.disableActeInput || disableInputOfManager()}
                            onChange={handleChangeTarif}
                            InputLabelProps={{
                                shrink: true,
                            }}
                        />
                    </Grid>
                    <Grid item xs={6} sm={6} md={2}>
                        <TextField
                            label="CA Budget"
                            color="warning"
                            fullWidth
                            error={errorInput.ca_budget}
                            value={budgetToSave?.ca_budget}
                            autoComplete="off"
                            placeholder="Ex: 2.02"
                            disabled
                            InputLabelProps={{
                                shrink: CheckAttr(budgetToSave?.ca_budget),
                            }}
                        />

                        <TextField
                            label="Heure Côut budget"
                            color="warning"
                            fullWidth
                            error={errorInput.heure_cout_budget}
                            name="heure_cout_budget"
                            value={budgetToSave?.heure_cout_budget}
                            autoComplete="off"
                            placeholder="Ex: 2.02"
                            onChange={handleChange}
                            InputLabelProps={{
                                shrink: CheckAttr(budgetToSave?.heure_cout_budget),
                            }}
                        />

                        <TextField
                            label="Coût/h"
                            color="warning"
                            fullWidth
                            value={coutParam?.coutactivite}
                            disabled={disableInputOfManager()}
                            onChange={handleChandeCout}
                            InputLabelProps={{
                                shrink: true,
                            }}
                        />

                        <TextField
                            label="Coût Budget"
                            color="warning"
                            fullWidth
                            // error={errorInput.ca_budget}
                            value={budgetToSave?.cout_budget}
                            autoComplete="off"
                            placeholder="Ex: 2.02"
                            disabled
                            InputLabelProps={{
                                shrink: CheckAttr(budgetToSave?.cout_budget),
                            }}
                        />

                        <TextField
                            label="Marge valeur budget"
                            color="warning"
                            fullWidth
                            disabled
                            value={budgetToSave?.marge_valeur_budget}
                            InputLabelProps={{
                                shrink: true,
                            }}
                        />

                        <TextField
                            label="Taux marge budget"
                            color="warning"
                            fullWidth
                            value={budgetToSave?.tx_marge_budget}
                            disabled
                            autoComplete="off"
                            placeholder="Ex: 2.02"
                            InputLabelProps={{
                                shrink: true,
                            }}
                        />

                        <TextField
                            label="DMT"
                            color="warning"
                            fullWidth
                            error={errorInput.dmt}
                            name="dmt"
                            type="time"
                            value={formatDmtForTimeInput(budgetToSave?.dmt)}
                            onChange={handleChange}
                            autoComplete="off"
                            placeholder="HH:MM:SS"
                            InputLabelProps={{
                                shrink: true,
                            }}
                            inputProps={{
                                step: 1,
                            }}
                        />

                        <TextField
                            label="Taux occupation (%)"
                            type="number"
                            color="warning"
                            fullWidth
                            error={errorInput.taux_occupation}
                            name="taux_occupation"
                            value={budgetToSave?.taux_occupation || ""}
                            onChange={handleChange}
                            autoComplete="off"
                            placeholder="Ex: 85"
                            InputLabelProps={{
                                shrink: true,
                            }}
                            inputProps={{
                                // step: 0.1,
                                min: 0,
                                max: 100,
                                lang: 'en-US'
                            }}
                        />

                        <TextField
                            label="Taux congés (%)"
                            type="number"
                            color="warning"
                            fullWidth
                            error={errorInput.taux_conges}
                            name="taux_conges"
                            value={budgetToSave?.taux_conges || ""}
                            onChange={handleChange}
                            autoComplete="off"
                            placeholder="Ex: 5"
                            InputLabelProps={{
                                shrink: true,
                            }}
                            inputProps={{
                                // step: 0.1,
                                min: 0,
                                max: 100,
                                lang: 'en-US'
                            }}
                        />

                        <TextField
                            label="Taux absentéisme (%)"
                            type="number"
                            color="warning"
                            fullWidth
                            error={errorInput.taux_absenteisme}
                            name="taux_absenteisme"
                            value={budgetToSave?.taux_absenteisme || ""}
                            onChange={handleChange}
                            autoComplete="off"
                            placeholder="Ex: 3"
                            InputLabelProps={{
                                shrink: true,
                            }}
                            inputProps={{
                                // step: 0.1,
                                min: 0,
                                max: 100,
                                lang: 'en-US'
                            }}
                        />
                    </Grid>
                </Grid>
                <Box display="flex" justifyContent="center" mt={5}>
                    <LoadingButton
                        onClick={() => handleSave()}
                        endIcon={<BorderColorIcon />}
                        loading={isClickSave}
                        loadingPosition="end"
                        sx={{ textTransform: "none" }}
                        variant="contained"
                        color="warning"
                    >
                        Enregistrer
                    </LoadingButton>
                </Box>
            </Box >
        </Fragment>
    );

    const content = isLoading ? progress : formContent;

    return content;
};

export default DuplicateBudgetForm;